/**
 * components/filters/PeriodFilter.js — ХУГАЦААНЫ ШҮҮЛТ: жил · улирал · сар.
 *
 * ⚠ ЯАГААД ХЭРЭГТЭЙ ВЭ: огнооны муж сонгогч (RangePicker) нь «3-р сар»
 *   гэх мэт ЭНГИЙН хугацааг сонгоход 4 товшилт шаарддаг (календарь нээх,
 *   эхлэл, төгсгөл, баталгаажуулах). Тайлан, хяналтын ажил бараг үргэлж
 *   САР · УЛИРАЛ · ЖИЛийн хүрээнд явагддаг тул эдгээрийг НЭГ товшилт
 *   болгоно.
 *
 * ⚠ НЭГ ЭХ СУРВАЛЖ: эдгээр сонгогч нь өөрийн төлөв хадгалдаггүй — бүгд
 *   `value` ([from, to] moment) руу бичиж, түүнээсээ эргэж уншина.
 *   Тусдаа төлөв барьвал RangePicker-ээр гараар муж сонгоход сонгогчид
 *   хуучин утгаа хадгалж, дэлгэц дээр ХОЁР өөр хугацаа харагдана.
 *
 * ⚠ САР ба УЛИРАЛ нь ХАРИЛЦАН ҮГҮЙСГЭНЭ. Хоёуланг зэрэг сонгуулбал
 *   «3-р сар + II улирал» гэсэн зөрчилтэй хослол үүсч, аль нь хүчинтэйг
 *   хэрэглэгч ч, код ч мэдэхгүй болно.
 */
import React, { useMemo } from "react";
import { Select } from "antd";
import moment from "moment";

const QUARTERS = [
  { value: 1, label: "I улирал", months: [0, 2] },
  { value: 2, label: "II улирал", months: [3, 5] },
  { value: 3, label: "III улирал", months: [6, 8] },
  { value: 4, label: "IV улирал", months: [9, 11] },
];

const MONTHS = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: `${i + 1}-р сар`,
}));

/** Хоёр moment нь тухайн мужийн ЯГ эхлэл/төгсгөл мөн эсэх. */
const isExact = (value, start, end) =>
  !!value &&
  !!value[0] &&
  !!value[1] &&
  value[0].isSame(start, "day") &&
  value[1].isSame(end, "day");

export default function PeriodFilter({
  value,
  onChange,
  yearsBack = 3,
  size,
}) {
  const thisYear = moment().year();

  const years = useMemo(() => {
    const out = [];
    for (let y = thisYear; y >= thisYear - yearsBack; y -= 1) out.push(y);
    return out;
  }, [thisYear, yearsBack]);

  /* Одоогийн мужаас жил · улирал · сарыг ЭРГЭЖ УНШИНА.
     Яг таарахгүй (гараар сонгосон) муж бол сонгогчид хоосон үлдэнэ —
     «сонгосон» мэт харагдвал хэрэглэгч буруу хугацаа харж байна гэж
     андуурна. */
  const picked = useMemo(() => {
    if (!value || !value[0] || !value[1]) {
      return { year: null, quarter: null, month: null };
    }
    const y = value[0].year();
    if (value[1].year() !== y) return { year: null, quarter: null, month: null };

    const yStart = moment([y, 0, 1]);
    const yEnd = moment([y, 11, 31]);
    if (isExact(value, yStart, yEnd)) {
      return { year: y, quarter: null, month: null };
    }
    for (const q of QUARTERS) {
      const s = moment([y, q.months[0], 1]);
      const e = moment([y, q.months[1], 1]).endOf("month");
      if (isExact(value, s, e)) return { year: y, quarter: q.value, month: null };
    }
    for (let m = 0; m < 12; m += 1) {
      const s = moment([y, m, 1]);
      const e = s.clone().endOf("month");
      if (isExact(value, s, e)) return { year: y, quarter: null, month: m + 1 };
    }
    return { year: null, quarter: null, month: null };
  }, [value]);

  // Сонгогч дээр жил заагаагүй бол одоогийн оныг суурь болгоно
  const baseYear = picked.year || thisYear;

  const setYear = (y) => {
    if (!y) return onChange?.(null);
    // Жил солиход ОДООГИЙН нарийвчлалыг хадгална (сар/улирал байвал тэр
    // хэвээр, зүгээр л өөр онд). Эс бөгөөс «2025 оны 3-р сар» гэж үзээд
    // жил солиход бүтэн жил болж, хэрэглэгч дахин сар сонгох шаардлагатай.
    if (picked.month) return setMonth(picked.month, y);
    if (picked.quarter) return setQuarter(picked.quarter, y);
    onChange?.([moment([y, 0, 1]), moment([y, 11, 31]).endOf("day")]);
  };

  const setQuarter = (q, y = baseYear) => {
    if (!q) return onChange?.([moment([y, 0, 1]), moment([y, 11, 31]).endOf("day")]);
    const def = QUARTERS.find((x) => x.value === q);
    onChange?.([
      moment([y, def.months[0], 1]),
      moment([y, def.months[1], 1]).endOf("month"),
    ]);
  };

  const setMonth = (m, y = baseYear) => {
    if (!m) return onChange?.([moment([y, 0, 1]), moment([y, 11, 31]).endOf("day")]);
    const s = moment([y, m - 1, 1]);
    onChange?.([s, s.clone().endOf("month")]);
  };

  const sel = { size, allowClear: true };

  return (
    <>
      <Select
        {...sel}
        placeholder="Жил"
        style={{ width: 92 }}
        value={picked.year || undefined}
        onChange={setYear}
        options={years.map((y) => ({ value: y, label: `${y}` }))}
      />
      <Select
        {...sel}
        placeholder="Улирал"
        style={{ width: 104 }}
        value={picked.quarter || undefined}
        onChange={(q) => setQuarter(q)}
        options={QUARTERS.map((q) => ({ value: q.value, label: q.label }))}
      />
      <Select
        {...sel}
        placeholder="Сар"
        style={{ width: 96 }}
        value={picked.month || undefined}
        onChange={(m) => setMonth(m)}
        options={MONTHS}
      />
    </>
  );
}
